const DEBUG = false;
import { classifyDrammenSykehusRoom } from "../../roomNumbering/drammenSykehusRoomNumbering";
import { buildNodeGrid, GRID_STEP_M } from "./buildNodeGrid.logic";
import { computePrevailingOrientation, projectPoint, unprojectPoint, } from "./autoPipeRouting.utils";
// ---------------------------------------------------------------------------
// Min-heap
// ---------------------------------------------------------------------------
class MinHeap {
    compare;
    items = [];
    constructor(compare) {
        this.compare = compare;
    }
    push(item) {
        this.items.push(item);
        this.bubbleUp(this.items.length - 1);
    }
    pop() {
        if (this.items.length === 0)
            return undefined;
        const top = this.items[0];
        const last = this.items.pop();
        if (this.items.length > 0) {
            this.items[0] = last;
            this.siftDown(0);
        }
        return top;
    }
    get size() {
        return this.items.length;
    }
    bubbleUp(i) {
        while (i > 0) {
            const parent = (i - 1) >> 1;
            if (this.compare(this.items[i], this.items[parent]) < 0) {
                [this.items[i], this.items[parent]] = [this.items[parent], this.items[i]];
                i = parent;
            }
            else {
                break;
            }
        }
    }
    siftDown(i) {
        const n = this.items.length;
        while (true) {
            let smallest = i;
            const l = 2 * i + 1;
            const r = 2 * i + 2;
            if (l < n && this.compare(this.items[l], this.items[smallest]) < 0)
                smallest = l;
            if (r < n && this.compare(this.items[r], this.items[smallest]) < 0)
                smallest = r;
            if (smallest === i)
                break;
            [this.items[i], this.items[smallest]] = [this.items[smallest], this.items[i]];
            i = smallest;
        }
    }
}
// ---------------------------------------------------------------------------
// Coordinate frame helpers
// ---------------------------------------------------------------------------
/**
 * Projects all polygon vertices, device coordinates, and the connection point
 * into the rotated frame whose X axis is `xAxis`.
 */
function projectArgs(args, xAxis) {
    const connectionPoints = args.connectionPoints;
    // TODO: Route against all connection points instead of using only the first one.
    const connectionPoint = connectionPoints[0];
    if (!connectionPoint) {
        throw new Error("projectArgs: at least one connection point is required");
    }
    return {
        ...args,
        selectedRooms: args.selectedRooms.map((room) => ({
            ...room,
            polygon: room.polygon.map((pt) => projectPoint([pt[0] ?? 0, pt[1] ?? 0], xAxis)),
        })),
        devicesToConnect: args.devicesToConnect.map((device) => ({
            ...device,
            coordinate: [
                ...projectPoint([device.coordinate[0] ?? 0, device.coordinate[1] ?? 0], xAxis),
                device.coordinate[2] ?? 0,
            ],
        })),
        connectionPoints: [
            [
                ...projectPoint([connectionPoint[0] ?? 0, connectionPoint[1] ?? 0], xAxis),
                connectionPoint[2] ?? 0,
            ],
        ],
    };
}
/**
 * Transforms all node positions in a routing graph back from the rotated frame
 * into world coordinates.  Edge data is unchanged.
 */
function unprojectGraph(graph, xAxis) {
    return {
        ...graph,
        nodes: graph.nodes.map((node) => {
            const [wx, wy] = unprojectPoint([node.posM[0] ?? 0, node.posM[1] ?? 0], xAxis);
            return { ...node, posM: [wx, wy, node.posM[2] ?? 0] };
        }),
    };
}
// ---------------------------------------------------------------------------
// Adjacency builder
// ---------------------------------------------------------------------------
function buildAdjacency(graph, args, corridorAxisNodes) {
    const adj = new Map();
    const { roomCrossingPenaltyM, corridorRoomFactor, blockedRoomFactor, verticalPenaltyFactor, perimeterPenaltyFactor } = args;
    if (roomCrossingPenaltyM == null ||
        verticalPenaltyFactor == null ||
        perimeterPenaltyFactor == null) {
        throw new Error("buildAdjacency: auto pipe routing cost settings are missing");
    }
    const corridorRoomNumbers = new Set();
    const blockedRoomNumbers = new Set();
    for (const r of args.selectedRooms ?? []) {
        const type = classifyDrammenSykehusRoom(r.roomName);
        if (type === "corridor")
            corridorRoomNumbers.add(r.roomNumber);
        if (type === "technical")
            blockedRoomNumbers.add(r.roomNumber);
    }
    const effectiveCorridorFactor = corridorRoomFactor ?? 0.5;
    const effectiveBlockedFactor = blockedRoomFactor ?? 100;
    console.info("buildAdjacency: cost settings used by routing algorithm", {
        roomCrossingPenaltyM,
        corridorRoomFactor: effectiveCorridorFactor,
        blockedRoomFactor: effectiveBlockedFactor,
        corridorRooms: [...corridorRoomNumbers],
        blockedRooms: [...blockedRoomNumbers],
        corridorAxisNodeCount: corridorAxisNodes.size,
        verticalPenaltyFactor,
        perimeterPenaltyFactor,
    });
    for (const node of graph.nodes) {
        adj.set(node.id, []);
    }
    const pos = new Map();
    const roomSets = new Map();
    const perimeterNodes = new Set();
    for (const node of graph.nodes) {
        pos.set(node.id, node.posM);
        roomSets.set(node.id, new Set(node.rooms ?? []));
        if (node.perimeter)
            perimeterNodes.add(node.id);
    }
    for (const edge of graph.edges) {
        const a = pos.get(edge.from);
        const b = pos.get(edge.to);
        const dx = a[0] - b[0], dy = a[1] - b[1], dz = a[2] - b[2];
        let tmpPerimeterFactor = 1;
        if (perimeterNodes.has(edge.from) || perimeterNodes.has(edge.to)) {
            tmpPerimeterFactor = perimeterPenaltyFactor;
        }
        const roomsA = roomSets.get(edge.from);
        const roomsB = roomSets.get(edge.to);
        const inBlocked = [...roomsA, ...roomsB].some((r) => blockedRoomNumbers.has(r));
        const onCorridorAxis = !inBlocked &&
            (corridorAxisNodes.has(edge.from) || corridorAxisNodes.has(edge.to));
        const roomTypeFactor = inBlocked
            ? effectiveBlockedFactor
            : onCorridorAxis
                ? effectiveCorridorFactor
                : 1;
        const hFactor = tmpPerimeterFactor * roomTypeFactor;
        const vFactor = verticalPenaltyFactor * roomTypeFactor;
        let cost = Math.sqrt((dx ** 2 + dy ** 2) * hFactor ** 2 + dz ** 2 * vFactor ** 2) || GRID_STEP_M;
        let crossings = 0;
        for (const r of roomsA)
            if (!roomsB.has(r))
                crossings++;
        for (const r of roomsB)
            if (!roomsA.has(r))
                crossings++;
        cost += crossings * roomCrossingPenaltyM * tmpPerimeterFactor * roomTypeFactor;
        adj.get(edge.from)?.push({ neighbor: edge.to, edgeId: edge.id, cost });
        adj.get(edge.to)?.push({ neighbor: edge.from, edgeId: edge.id, cost });
    }
    return adj;
}
// ---------------------------------------------------------------------------
// Public entry points
// ---------------------------------------------------------------------------
/**
 * Returns one IRoutingGraph per routing step, where each step adds the next
 * MST edge (or all Steiner paths at once).  The last element is the complete
 * routed tree.  Suitable for animated rendering: render frame N, wait, render
 * frame N+1, etc.
 *
 * With K devices + 1 connection point there are K steps (K-1 MST edges plus
 * the final connection-point leg), or 1 step when a Steiner point is used.
 */
export function buildRoutingIterations(args) {
    const { projectNo, floorName, pipeElevationsM } = args;
    // Align the grid with the prevailing room orientation so pipes run parallel
    // to the dominant wall direction rather than the world X/Y axes.
    const xAxis = computePrevailingOrientation(args.selectedRooms);
    const projectedArgs = projectArgs(args, xAxis);
    const corridorRoomNumbers = new Set(projectedArgs.selectedRooms
        .filter((r) => classifyDrammenSykehusRoom(r.roomName) === "corridor")
        .map((r) => r.roomNumber));
    const { graph: grid, corridorAxisNodeIds } = buildNodeGrid(projectedArgs, undefined, undefined, corridorRoomNumbers);
    if (grid.nodes.length === 0) {
        return [{ projectNo, systemCode: "AUTO_PIPE_ROUTING", floorName, nodes: [], edges: [] }];
    }
    const adj = buildAdjacency(grid, projectedArgs, corridorAxisNodeIds);
    if (DEBUG)
        writeGraphDebugLog(grid, adj);
    const edgeLookup = buildEdgeLookup(grid);
    const sources = buildSources(projectedArgs, grid, pipeElevationsM.xDirection);
    if (sources.length < 2) {
        return [{ projectNo, systemCode: "AUTO_PIPE_ROUTING", floorName, nodes: [], edges: [] }];
    }
    const { steps } = runMultiSourceDijkstra(sources, adj, edgeLookup);
    // Build cumulative snapshots: each frame includes all edges from previous steps.
    const accumulated = new Set();
    return steps.map((step) => {
        for (const id of step)
            accumulated.add(id);
        const graph = assembleResult(projectedArgs, grid, sources, new Set(accumulated), pipeElevationsM.xDirection);
        return unprojectGraph(graph, xAxis);
    });
}
/**
 * Routes pipes and returns the completed tree in a single call.
 *
 * Algorithm:
 *  1. Build a node grid covering all selected rooms.
 *  2. Identify one cluster root per device + one for the connectionPoint
 *     (each mapped to the nearest grid node).
 *  3. Run a single-source Dijkstra from every cluster root.
 *  4. Build a minimum spanning tree on the cluster graph using pairwise
 *     shortest-path distances (min_v d_i(v) + d_j(v)).
 *  5. For exactly 3 clusters, also evaluate a Steiner point:
 *     min_v (d_A(v) + d_B(v) + d_C(v)).  Use it when cheaper than the MST.
 *  6. Trace every selected path by gradient descent on the distance fields.
 *  7. Emit the union of all path edges as the result graph, augmented with
 *     DEVICE_PORT nodes at the actual device coordinates.
 */
export function routeDevices(args) {
    const iterations = buildRoutingIterations(args);
    return (iterations.at(-1) ?? {
        projectNo: args.projectNo,
        systemCode: "AUTO_PIPE_ROUTING",
        floorName: args.floorName,
        nodes: [],
        edges: [],
    });
}
// ---------------------------------------------------------------------------
// Graph utilities
// ---------------------------------------------------------------------------
/**
 * Writes a JSON debug log of the routing graph to `./routing-graph-debug.json`
 * in the process working directory.  Silently skips if not in a Node.js env.
 *
 * Format:
 *   nodes[]: { id, pos:[x,y,z], rooms, perimeter }
 *   edges[]: { id, from, to, cost }
 */
function writeGraphDebugLog(graph, adj) {
    const nodes = graph.nodes.map((n) => ({
        id: n.id,
        pos: n.posM,
        rooms: n.rooms ?? [],
        perimeter: n.perimeter ?? false,
    }));
    const edges = graph.edges.map((e) => {
        const entry = adj.get(e.from)?.find((a) => a.neighbor === e.to);
        return { id: e.id, from: e.from, to: e.to, cost: entry?.cost ?? null };
    });
    const json = JSON.stringify({ nodes, edges }, null, 2);
    // Browser: trigger a file download.
    if (typeof document !== "undefined") {
        const blob = new Blob([json], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "routing-graph-debug.json";
        a.click();
        URL.revokeObjectURL(url);
        return;
    }
    // Node.js fallback (server-side rendering, tests, etc.).
    try {
        // eslint-disable-next-line @typescript-eslint/no-implied-eval, no-new-func
        const r = new Function("m", "return require(m)");
        const fs = r("fs");
        const path = r("path");
        const cwd = r("process").cwd();
        fs.writeFileSync(path.join(cwd, "routing-graph-debug.json"), json);
    }
    catch { /* not in Node.js */ }
}
/** Returns Map<"nodeA,nodeB", edgeId> for fast edge ID lookup during path tracing. */
function buildEdgeLookup(graph) {
    const lookup = new Map();
    for (const edge of graph.edges) {
        lookup.set(`${edge.from},${edge.to}`, edge.id);
        lookup.set(`${edge.to},${edge.from}`, edge.id);
    }
    return lookup;
}
// ---------------------------------------------------------------------------
// Source / cluster definitions
// ---------------------------------------------------------------------------
function buildSources(args, grid, elevationM) {
    const sources = [];
    for (let i = 0; i < args.devicesToConnect.length; i++) {
        const device = args.devicesToConnect[i];
        const posM = [
            device.coordinate[0] ?? 0,
            device.coordinate[1] ?? 0,
            elevationM,
        ];
        const nodeId = nearestGridNode(posM, grid);
        if (nodeId) {
            sources.push({
                idx: i,
                nodeId,
                posM,
                deviceRef: {
                    deviceId: String(device._id ?? `auto-device-${i}`),
                    compCodeSnapshot: device.compCode,
                    port: "OUT",
                },
            });
        }
    }
    // Connection point as the last cluster.
    const connectionPoints = args.connectionPoints;
    // TODO: Route against all connection points instead of using only the first one.
    const connectionPoint = connectionPoints[0];
    if (!connectionPoint) {
        throw new Error("buildSources: at least one connection point is required");
    }
    const cpPos = [
        connectionPoint[0] ?? 0,
        connectionPoint[1] ?? 0,
        elevationM,
    ];
    const cpNode = nearestGridNode(cpPos, grid);
    if (cpNode) {
        sources.push({ idx: args.devicesToConnect.length, nodeId: cpNode, posM: cpPos });
    }
    return sources;
}
function nearestGridNode(pos, grid) {
    let bestId = null;
    let bestDist = Infinity;
    for (const node of grid.nodes) {
        const dx = node.posM[0] - pos[0];
        const dy = node.posM[1] - pos[1];
        const dz = node.posM[2] - pos[2];
        const d2 = dx * dx + dy * dy + dz * dz;
        if (d2 < bestDist) {
            bestDist = d2;
            bestId = node.id;
        }
    }
    return bestId;
}
/** Canonical sorted key for a cluster pair. */
function pairKey(a, b) {
    return a < b ? `${a},${b}` : `${b},${a}`;
}
/**
 * Resolves a cluster ID to its canonical root via the merge table.
 * Uses path-compression: shortcuts intermediate links on each traversal.
 */
function canonicalCluster(id, mergedInto) {
    let cur = id;
    while (mergedInto.has(cur)) {
        const parent = mergedInto.get(cur);
        // Path compression: point directly to grandparent when available.
        const grandparent = mergedInto.get(parent);
        if (grandparent !== undefined)
            mergedInto.set(cur, grandparent);
        cur = parent;
    }
    return cur;
}
/**
 * Runs all cluster wavefronts simultaneously through one shared min-heap and
 * records pairwise distances inline.
 *
 * Every source is seeded at distance 0.  The heap pops globally by distance,
 * so a node is settled for each cluster the moment that cluster's wavefront
 * definitively reaches it.
 *
 * After settling (nodeId, cId) we immediately scan the node's existing entries:
 * any other canonical cluster already there gives us a candidate pairwise
 * distance `dist_cId + dist_other`.  We keep the minimum across all meeting
 * nodes seen so far.  Because Dijkstra processes in non-decreasing distance
 * order, once both wavefronts have passed a node the distance recorded at that
 * node is final — no later meeting node can improve on what was already found
 * for the same pair at an earlier (cheaper) node.
 */
/**
 * After absorbing `absorbed` into `canonical`, propagate pairwise distances:
 * for every other cluster X, dist(canonical,X) = min(old, dist(absorbed,X)).
 */
function updatePairwiseAfterMerge(canonical, absorbed, pairwiseDist, sources, mergedInto) {
    for (let k = 0; k < sources.length; k++) {
        const x = canonicalCluster(k, mergedInto);
        if (x === canonical)
            continue;
        const fromAbsorbed = pairwiseDist.get(pairKey(absorbed, x));
        if (!fromAbsorbed)
            continue;
        const keyFromCanon = pairKey(canonical, x);
        const fromCanon = pairwiseDist.get(keyFromCanon);
        if (!fromCanon || fromAbsorbed.dist < fromCanon.dist)
            pairwiseDist.set(keyFromCanon, fromAbsorbed);
    }
}
/**
 * Remove Steiner candidates that no longer span 3 distinct canonical clusters.
 * Update stored cluster IDs to current canonical roots and recalculate totals.
 */
function cullSteinerCandidates(candidates, mergedInto) {
    for (let i = candidates.length - 1; i >= 0; i--) {
        const c = candidates[i];
        // Deduplicate by canonical ID, keeping minimum distance per canonical.
        const seen = new Map();
        for (let k = 0; k < 3; k++) {
            const cId = canonicalCluster(c.clusters[k], mergedInto);
            const d = c.dists[k];
            const prev = seen.get(cId);
            if (prev === undefined || d < prev)
                seen.set(cId, d);
        }
        if (seen.size < 3) {
            candidates.splice(i, 1);
            continue;
        }
        const s = [...seen.entries()].sort((a, b) => a[1] - b[1]);
        c.clusters = [s[0][0], s[1][0], s[2][0]];
        c.dists = [s[0][1], s[1][1], s[2][1]];
        c.totalDist = s[0][1] + s[1][1] + s[2][1];
        c.twoLargestSum = s[1][1] + s[2][1];
    }
}
function runMultiSourceDijkstra(sources, adj, edgeLookup) {
    const settled = new Map();
    const pairwiseDist = new Map();
    const steinerCandidates = [];
    const mergedInto = new Map();
    const heap = new MinHeap((a, b) => a.dist - b.dist);
    const steps = [];
    // Track which canonical cluster IDs are still active (not absorbed).
    const activeCanonicals = new Set(sources.map((_, k) => k));
    // Reverse edge lookup: edgeId → [nodeA, nodeB]. Used to derive pathNodes
    // from the XOR'd step edges so that only drawn nodes are re-seeded at dist=0.
    const edgeToNodes = new Map();
    for (const [key, edgeId] of edgeLookup) {
        if (!edgeToNodes.has(edgeId)) {
            const comma = key.indexOf(",");
            edgeToNodes.set(edgeId, [key.slice(0, comma), key.slice(comma + 1)]);
        }
    }
    for (let clusterId = 0; clusterId < sources.length; clusterId++) {
        heap.push({ dist: 0, nodeId: sources[clusterId].nodeId, clusterId });
    }
    while (heap.size > 0 && activeCanonicals.size > 1) {
        const { dist, nodeId, clusterId: rawId } = heap.pop();
        const cId = canonicalCluster(rawId, mergedInto);
        if (dist >= (settled.get(nodeId)?.get(cId) ?? Infinity))
            continue;
        if (!settled.has(nodeId))
            settled.set(nodeId, new Map());
        settled.get(nodeId).set(cId, dist);
        // Canonical others already settled at this node.
        const canonOthers = new Map();
        for (const [rawOtherId, otherDist] of settled.get(nodeId)) {
            if (rawOtherId === cId)
                continue;
            const otherId = canonicalCluster(rawOtherId, mergedInto);
            if (otherId === cId)
                continue;
            const prev = canonOthers.get(otherId);
            if (prev === undefined || otherDist < prev)
                canonOthers.set(otherId, otherDist);
        }
        // Update pairwise distances.
        for (const [otherId, otherDist] of canonOthers) {
            const total = dist + otherDist;
            const key = pairKey(cId, otherId);
            const existing = pairwiseDist.get(key);
            if (!existing || total < existing.dist)
                pairwiseDist.set(key, { dist: total, via: nodeId });
        }
        // Add Steiner candidate when this becomes the 3rd distinct canonical cluster at this node.
        const rawSize = settled.get(nodeId).size;
        if (rawSize === 3 && canonOthers.size === 2) {
            const [[id1, d1], [id2, d2]] = [...canonOthers.entries()];
            const arms = [[cId, dist], [id1, d1], [id2, d2]];
            arms.sort((a, b) => a[1] - b[1]);
            insertSteinerSorted(steinerCandidates, {
                nodeId,
                clusters: [arms[0][0], arms[1][0], arms[2][0]],
                dists: [arms[0][1], arms[1][1], arms[2][1]],
                totalDist: arms[0][1] + arms[1][1] + arms[2][1],
                twoLargestSum: arms[1][1] + arms[2][1],
            });
        }
        // ── Check merge condition ────────────────────────────────────────────────
        // d is how far we have searched.  Once d ≥ the cheapest pending connection
        // cost, that connection is guaranteed optimal — commit it.
        if (steinerCandidates.length > 0) {
            const best = steinerCandidates[0];
            const [cA, cB, cC] = best.clusters;
            const ab = pairwiseDist.get(pairKey(cA, cB))?.dist ?? Infinity;
            const ac = pairwiseDist.get(pairKey(cA, cC))?.dist ?? Infinity;
            const bc = pairwiseDist.get(pairKey(cB, cC))?.dist ?? Infinity;
            // MST cost for 3 clusters = sum of 2 smallest pairwise distances.
            const pairsSorted = [ab, ac, bc].filter(isFinite).sort((a, b) => a - b);
            const mstCost = pairsSorted.length >= 2 ? pairsSorted[0] + pairsSorted[1] : Infinity;
            const mergeThreshold = Math.min(best.totalDist, mstCost);
            if (dist >= mergeThreshold) {
                const step = new Set();
                const pathNodes = new Set();
                let newCanonical;
                if (best.totalDist <= mstCost) {
                    // Partial steiner: trace from the steiner node toward the 2 closest clusters,
                    // XOR the edge sets to remove the shared trunk (edges common to both paths).
                    // Derive pathNodes only from the XOR'd edges so the shared trunk nodes (steiner
                    // point and any common prefix) are NOT re-seeded at dist=0 — re-seeding them
                    // would create false dist=0 anchors that corrupt subsequent traces.
                    const pathA = new Set();
                    const pathB = new Set();
                    tracePath(best.nodeId, mergedClusterDistField(settled, best.clusters[0], mergedInto), adj, edgeLookup, pathA);
                    tracePath(best.nodeId, mergedClusterDistField(settled, best.clusters[1], mergedInto), adj, edgeLookup, pathB);
                    for (const e of pathA)
                        if (!pathB.has(e))
                            step.add(e);
                    for (const e of pathB)
                        if (!pathA.has(e))
                            step.add(e);
                    for (const e of step) {
                        const nodes = edgeToNodes.get(e);
                        if (nodes) {
                            pathNodes.add(nodes[0]);
                            pathNodes.add(nodes[1]);
                        }
                    }
                    newCanonical = cA;
                    mergedInto.set(cB, cA);
                    updatePairwiseAfterMerge(cA, cB, pairwiseDist, sources, mergedInto);
                    activeCanonicals.delete(cB);
                    // cC stays active — pairwise distances for cA vs remaining clusters are updated
                    // naturally by the Dijkstra propagation from the re-seeded dist=0 path nodes.
                }
                else {
                    // Pairwise: connect the two closest clusters among the three.
                    const pairsArr = [
                        { d: ab, i: cA, j: cB }, { d: ac, i: cA, j: cC }, { d: bc, i: cB, j: cC },
                    ].filter(p => isFinite(p.d)).sort((a, b) => a.d - b.d);
                    const closest = pairsArr[0];
                    const via = pairwiseDist.get(pairKey(closest.i, closest.j)).via;
                    tracePath(via, mergedClusterDistField(settled, closest.i, mergedInto), adj, edgeLookup, step, pathNodes);
                    tracePath(via, mergedClusterDistField(settled, closest.j, mergedInto), adj, edgeLookup, step, pathNodes);
                    newCanonical = closest.i;
                    mergedInto.set(closest.j, closest.i);
                    updatePairwiseAfterMerge(closest.i, closest.j, pairwiseDist, sources, mergedInto);
                    activeCanonicals.delete(closest.j);
                }
                steps.push(step);
                for (const pn of pathNodes)
                    heap.push({ dist: 0, nodeId: pn, clusterId: newCanonical });
                cullSteinerCandidates(steinerCandidates, mergedInto);
            }
        }
        else if (activeCanonicals.size >= 2) {
            // No Steiner candidates — find the cheapest known pairwise connection
            // among all remaining active clusters and merge it when optimal.
            const active = [...activeCanonicals];
            let bestPair = null;
            for (let i = 0; i < active.length; i++) {
                for (let j = i + 1; j < active.length; j++) {
                    const pair = pairwiseDist.get(pairKey(active[i], active[j]));
                    if (pair && (!bestPair || pair.dist < bestPair.dist))
                        bestPair = { c0: active[i], c1: active[j], dist: pair.dist, via: pair.via };
                }
            }
            if (bestPair && dist >= bestPair.dist) {
                const step = new Set();
                const pathNodes = new Set();
                tracePath(bestPair.via, mergedClusterDistField(settled, bestPair.c0, mergedInto), adj, edgeLookup, step, pathNodes);
                tracePath(bestPair.via, mergedClusterDistField(settled, bestPair.c1, mergedInto), adj, edgeLookup, step, pathNodes);
                mergedInto.set(bestPair.c1, bestPair.c0);
                activeCanonicals.delete(bestPair.c1);
                updatePairwiseAfterMerge(bestPair.c0, bestPair.c1, pairwiseDist, sources, mergedInto);
                steps.push(step);
                for (const pn of pathNodes)
                    heap.push({ dist: 0, nodeId: pn, clusterId: bestPair.c0 });
                cullSteinerCandidates(steinerCandidates, mergedInto);
            }
        }
        // Expand to unsettled neighbours.
        for (const { neighbor, cost } of adj.get(nodeId) ?? []) {
            if (!settled.get(neighbor)?.has(cId))
                heap.push({ dist: dist + cost, nodeId: neighbor, clusterId: cId });
        }
    }
    return { steps };
}
/**
 * Inserts a SteinerCandidate into a list that is kept sorted ascending by
 * totalDist, using binary search to find the insertion point.
 */
function insertSteinerSorted(list, candidate) {
    let lo = 0;
    let hi = list.length;
    while (lo < hi) {
        const mid = (lo + hi) >>> 1;
        if (list[mid].totalDist <= candidate.totalDist)
            lo = mid + 1;
        else
            hi = mid;
    }
    list.splice(lo, 0, candidate);
}
/**
 * Like clusterDistField but also includes every raw cluster that has been
 * absorbed into `canonical` (via mergedInto), taking the minimum distance
 * across all matching raw clusters at each node.
 *
 * This is necessary at merge time: after cluster B is merged into canonical A,
 * B's territory is still stored under key B in `settled`.  Using this function
 * allows tracePath to follow the gradient through B's territory back to the
 * nearest dist-0 node in the combined A+B region.
 */
function mergedClusterDistField(settled, canonical, mergedInto) {
    const out = new Map();
    for (const [nodeId, byCluster] of settled) {
        for (const [rawId, d] of byCluster) {
            if (canonicalCluster(rawId, mergedInto) !== canonical)
                continue;
            const prev = out.get(nodeId);
            if (prev === undefined || d < prev)
                out.set(nodeId, d);
        }
    }
    return out;
}
// ---------------------------------------------------------------------------
// Path tracing (gradient descent on a distance field)
// ---------------------------------------------------------------------------
/**
 * Traces from `target` toward the nearest distance-0 node in `distField` by
 * gradient descent, adding each crossed edge to `edgesOut`.
 * When `nodesOut` is provided, every visited node is collected (used for
 * re-seeding the merged cluster after a connection is committed).
 */
function tracePath(target, distField, adj, edgeLookup, edgesOut, nodesOut) {
    let current = target;
    nodesOut?.add(current);
    const visited = new Set();
    while ((distField.get(current) ?? Infinity) > 0) {
        if (visited.has(current))
            break;
        visited.add(current);
        const currentDist = distField.get(current) ?? Infinity;
        let bestNeighbor = null;
        let bestDist = Infinity;
        for (const { neighbor, cost } of adj.get(current) ?? []) {
            const nd = distField.get(neighbor) ?? Infinity;
            if (nd + cost <= currentDist + 1e-9 && nd < bestDist) {
                bestDist = nd;
                bestNeighbor = neighbor;
            }
        }
        if (bestNeighbor === null)
            break;
        const edgeId = edgeLookup.get(`${current},${bestNeighbor}`);
        if (edgeId)
            edgesOut.add(edgeId);
        nodesOut?.add(bestNeighbor);
        current = bestNeighbor;
    }
}
// ---------------------------------------------------------------------------
// Result graph assembly
// ---------------------------------------------------------------------------
function assembleResult(args, grid, sources, pathEdgeIds, _elevationM) {
    const devices = args.devicesToConnect;
    // Collect grid edges on the selected paths.
    const selectedEdges = grid.edges.filter((e) => pathEdgeIds.has(e.id));
    // Collect the grid node IDs referenced by those edges.
    const usedNodeIds = new Set();
    for (const edge of selectedEdges) {
        usedNodeIds.add(edge.from);
        usedNodeIds.add(edge.to);
    }
    // Also include source root nodes even if they ended up isolated
    // (e.g. two devices snapped to the same grid node).
    for (const src of sources)
        usedNodeIds.add(src.nodeId);
    // Grid junction nodes.
    const nodes = grid.nodes
        .filter((n) => usedNodeIds.has(n.id))
        .map((n) => ({ ...n, kind: "JUNCTION" }));
    const edges = [...selectedEdges];
    // -------------------------------------------------------------------------
    // Add DEVICE_PORT nodes and connecting edges for each device.
    // -------------------------------------------------------------------------
    for (let i = 0; i < devices.length; i++) {
        const device = devices[i];
        const src = sources.find((s) => s.idx === i);
        if (!src)
            continue;
        const deviceNodeId = `rp-device-${i}`;
        const devicePos = [
            device.coordinate[0] ?? 0,
            device.coordinate[1] ?? 0,
            device.coordinate[2] ?? 0,
        ];
        nodes.push({
            id: deviceNodeId,
            posM: devicePos,
            kind: "DEVICE_PORT",
            deviceRef: src.deviceRef,
        });
        // Short riser edge: device port → nearest grid node (at pipe elevation).
        edges.push({
            id: `rp-device-edge-${i}`,
            from: deviceNodeId,
            to: src.nodeId,
            compCode: `AUTO-PIPE-RISER-${i + 1}`,
            profile: "CIRCULAR",
            size: { diameterM: 0.1 },
        });
    }
    return {
        projectNo: args.projectNo,
        systemCode: "AUTO_PIPE_ROUTING",
        systemName: "Auto pipe routing",
        floorName: args.floorName,
        roomNumber: args.selectedRooms[0]?.roomNumber,
        nodes,
        edges,
    };
}
